import Link from "next/link";

import { Heading, Text } from "@/src/components/ui/Typography";
import { LazyImage } from "@/src/components/features/common/LazyImage";
import { CursorFollowBadge, CursorTrackArea } from "@/src/components/molecules/CursorFollowBadge";
import { paths } from "@/src/routes/paths";
import type { Post } from "@/src/lib/blog/posts";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface BlogPostCardProps {
  post: Post;
  priority?: boolean;
}

export function BlogPostCard({ post, priority = false }: BlogPostCardProps) {
  return (
    <Link href={paths.blog.post(post.slug)} className="group flex flex-col">
      <CursorTrackArea className="w-full overflow-hidden rounded-(--radius-lg)">
        <LazyImage
          src={post.coverThumbnail}
          alt={post.coverAlt}
          aspectRatio="16/10"
          priority={priority}
          // coverThumbnail is already server-generated at the right size and
          // encoding — next/image's own re-optimization step has been
          // producing visibly worse output than the source file in
          // production, so skip it and serve the original bytes.
          unoptimized
        />
        <CursorFollowBadge label="Read Article" className="hidden md:flex" />
      </CursorTrackArea>

      <Heading
        as="h3"
        size="headingMD"
        className="mt-5 line-clamp-2 min-h-[60px] text-neutral-700 transition-colors duration-300 group-hover:text-brand md:min-h-[76px]"
      >
        {post.title}
      </Heading>

      <div className="mt-3 flex items-center gap-1.5 text-neutral-500">
        {post.authorAvatar && (
          <img
            src={post.authorAvatar}
            alt=""
            className="h-[18px] w-[18px] shrink-0 rounded-full object-cover"
          />
        )}
        <Text as="span" size="bodySM" className="truncate">
          {post.authorName}
        </Text>
        <span aria-hidden className="text-neutral-700">
          ·
        </span>
        <Text as="span" size="bodySM" className="whitespace-nowrap">
          <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
        </Text>
        <span aria-hidden className="text-neutral-700">
          ·
        </span>
        <Text as="span" size="bodySMMedium" className="whitespace-nowrap text-neutral-600 font-light">
          {post.readTime} min read
        </Text>
      </div>
    </Link>
  );
}
